import { ExternalLink, RefreshCw, WifiOff } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import crmOrbitaConnectionService from '../../domain/services/crm/crm.orbita.connection.service';
import useOrbitaConnectionGate from '../../hooks/crm/useOrbitaConnectionGate';
import {
    ORBITA_GATED_PATH_PREFIX,
    ORBITA_RECONNECT_URL,
    orbitaConnectionTexts as texts
} from '../../domain/constant/crm/crm.orbita.connection.constant';

const OrbitaDisconnectedBlock = ({ checking, stillDisconnected, onRecheck }) => (
    <section
        role="alert"
        data-testid="orbita-connection-gate"
        className="mx-auto max-w-[560px] rounded-xl border border-red-200 bg-white px-8 py-10 text-center shadow-sm"
    >
        <span className="mx-auto mb-5 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-red-50 text-red-600">
            <WifiOff className="h-[32px] w-[32px]" />
        </span>

        <h2 className="mb-2 text-[20px] font-semibold text-gray-900">{texts.title}</h2>
        <p className="mx-auto mb-6 max-w-[440px] text-[14px] text-gray-600">{texts.description}</p>

        <ol className="mx-auto mb-7 max-w-[420px] space-y-2 text-left text-[14px] text-gray-700">
            {texts.steps.map((step, index) => (
                <li key={step} className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[12px] font-semibold text-gray-700">
                        {index + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                </li>
            ))}
        </ol>

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
                href={ORBITA_RECONNECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-700"
            >
                <ExternalLink className="h-4 w-4" />
                {texts.openOrbita}
            </a>
            <button
                type="button"
                disabled={checking}
                onClick={onRecheck}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
                <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
                {checking ? texts.checking : texts.recheck}
            </button>
        </div>

        {stillDisconnected && (
            <p className="mt-4 text-[13px] font-medium text-red-600">{texts.stillDisconnected}</p>
        )}
    </section>
);

// F188: no Gestto, vendedor com conexão própria só opera com o WhatsApp conectado ao Órbita.
const OrbitaConnectionGate = ({ children }) => {
    const { pathname } = useLocation();
    const enabled =
        pathname.startsWith(ORBITA_GATED_PATH_PREFIX) && crmOrbitaConnectionService.isRequired();
    const { blocked, checking, stillDisconnected, recheck } = useOrbitaConnectionGate(enabled);

    // Mesmo invólucro bloqueado ou não: a página não desmonta e o que o vendedor digitou continua lá.
    // O aviso vai por portal acima de gavetas e modais (SideDrawer usa z-index 100010).
    return (
        <>
            <div
                aria-hidden={blocked ? 'true' : undefined}
                inert={blocked ? '' : undefined}
                className={blocked ? 'pointer-events-none select-none blur-[2px]' : undefined}
            >
                {children}
            </div>
            {blocked &&
                createPortal(
                    <div className="fixed inset-0 z-[100050] overflow-y-auto bg-white/70 px-4 pt-24">
                        <OrbitaDisconnectedBlock
                            checking={checking}
                            stillDisconnected={stillDisconnected}
                            onRecheck={recheck}
                        />
                    </div>,
                    document.body
                )}
        </>
    );
};

export default OrbitaConnectionGate;
